import {Component, OnInit} from '@angular/core';
import {Billingservices} from '../../../model/Billingservices';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {AuthService} from '../../../service/auth/auth.service';
import {Constant} from '../../../../global/util/Constant';
import {Partner} from '../../../model/Partner';
import {Enterprise} from '../../../model/Enterprise';
import {LocalStorage} from '../../../../global/util/LocalStorage';
import {Dialog} from '../../../../global/util/Dialog';
import {InformType} from '../../../../global/util/enum/InformType';
import {Billingservicestype} from '../../../model/Billingservicestype';
import {TranslateService} from '@ngx-translate/core';
import {BillingService} from '../../../service/billing.service';
import {EnterpriseService} from '../../../service/enterprise.service';
import {EnterpriseDataService} from '../../../service/data/enterprise-data.service';

@Component({
  selector: 'app-billing-service',
  templateUrl: './billing-services-add.component.html',
  styleUrls: ['./billing-services-add.component.css']
})
export class BillingServicesAddComponent implements OnInit {
  public loading: boolean;
  public ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  public ROLE_PARTNER = Constant.ROLE_PARTNER;
  public billingservices: Billingservices | null = null;
  public billingForm!: FormGroup;
  public partnerSelected: Partner;
  public partnerIdSelected = 0;
  public enterpriseIdSelected = 0;
  public enterprise: Enterprise;
  public partners: Partner[] = [];
  public enterprises: Enterprise[] = [];
  public billingServicesTypes: Billingservicestype[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private authenticationService: AuthService,
    private router: Router,
    private billingService: BillingService,
    private enterpriseService: EnterpriseService,
    private enterpriseDataService: EnterpriseDataService,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.enterprises = LocalStorage.getDefaultEnterprise();
    this.setDefaultData();
    this.createForm();
    this.billingForm.get('partner')?.valueChanges.subscribe(partnerId => {
      this.getEnterprises(partnerId);
    });

    this.billingForm.get('discountPartner')?.valueChanges.subscribe((checked: boolean) => {
      this.updateValidators(checked);
    });
    this.loadBillingServicesType();
  }

  private setDefaultData() {
    this.partners = LocalStorage.getPartnersExtra();
    this.partnerIdSelected = this.partners[Constant.ZERO_INDEX].id;
    if (this.hasRole(this.ROLE_SUPERADMIN)) {
      this.getAllEnterprisesBy();
    } else {
      this.getEnterprisesByPartner(this.partnerIdSelected);
    }
  }

  private getEnterprises(partnerId: number) {
    this.getEnterpriseBy(partnerId);
  }

  private updateValidators(value: boolean) {
    if (value) {
      this.billingForm.get('amount').clearValidators();
      this.billingForm.get('amount').updateValueAndValidity();
    } else {
      this.billingForm.get('amount').setValidators([Validators.required]);
      this.billingForm.get('amount').updateValueAndValidity();
    }
  }


  public isInvalidControl(ctrlName: string) {
    // @ts-ignore
    return this.billingForm.get(ctrlName).invalid && this.billingForm.get(ctrlName).touched;
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  public isDiscountPartner() {
    return this.billingForm.value.discountPartner;
  }

  private loadBillingServicesType(): void {
    this.loading = true;
    const url = Constant.ROOT_API_V1 + '/billingservices/type';
    this.billingService.get(url).subscribe((data: Billingservicestype[]) => {
      this.loading = false;
      if (data.length > 0) {
        this.billingServicesTypes = data;
        const unique: Billingservicestype[] = [{
          id: 0,
          name: 'SELECCIONE',
          description: 'SELECCIONE',
        }];
        this.billingServicesTypes = unique.concat(this.billingServicesTypes);
      } else {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.BILLING-SERVICES'), this.translate.instant('MESSAGE.DOES-NOT-HAVE-BILLING'));
      }
    }, () => {
      this.loading = false;
    });
  }

  private createForm(): void {
    this.billingForm = this.formBuilder.group({
      partner: [this.billingservices ? this.billingservices.partnerId : 0, [this.notEqualToDefault()]],
      enterprise: [this.billingservices ? this.billingservices.enterpriseId : 0, [this.notEqualToDefault()]],
      billingServiceType: [this.billingservices ? this.billingservices.billingservicetypeId : 0, [this.notEqualToDefault()]],
      amount: ['', [Validators.required, Validators.min(1)]],
      active: [this.billingservices ? this.billingservices.active : true, []],
      discountPartner: [this.billingservices ? this.billingservices.discountPartner : false, []],
    });
  }

  notEqualToDefault() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === 0) {
        return {notAllowedValue: true};
      }
      return null;
    };
  }

  cancel(): void {
    this.router.navigate(['/billing-services']).then();
  }

  save(): void {
    if (this.billingForm.invalid) {
      Object.values(this.billingForm.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }
    const formValue = this.billingForm.value;
    const billingservices: Billingservices = {
      id: this.billingservices ? this.billingservices.id : 0,
      enterpriseId: formValue.enterprise,
      partnerId: formValue.partner,
      billingservicetypeId: formValue.billingServiceType,
      disabled: false,
      description: '',
      assignedCredit: formValue.amount,
      currentCredit: formValue.amount,
      active: formValue.active,
      discountPartner: formValue.discountPartner,
      deleted: false,
      createAt: this.billingservices ? this.billingservices.createAt : new Date().toISOString(),
      disabledAt: this.billingservices ? this.billingservices.createAt : new Date().toISOString(),
    };
    if (this.billingservices) {
      this.updateBillingService(billingservices);
    } else {
      this.createBillingService(billingservices);
    }
  }

  private createBillingService(billingservices: Billingservices): void {
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/billingservices');
    this.billingService.post(url, billingservices).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigate(['/billing-services']).then();
      },
      error: () => {
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });

  }

  private updateBillingService(billingservices: Billingservices): void {
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/billingservices');
    this.billingService.put(url, billingservices).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigate(['/billing-services']).then();
      },
      error: () => {
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });

  }

  private getAllEnterprisesBy() {
    this.enterpriseService.getAllEnterprises().subscribe(enterprises => {
      this.enterpriseDataService.setEnterprises(enterprises.sort());
      this.enterprises = LocalStorage.addEnterprise(enterprises).sort();
      this.enterpriseIdSelected = this.enterprises[Constant.ZERO_INDEX].id;
      this.getEnterpriseBy(this.partnerIdSelected);
    });
  }

  private getEnterprisesByPartner(partnerId: number) {
    this.enterpriseService.getEnterprisesByPartner(partnerId).subscribe(enterprises => {
      this.enterpriseDataService.setEnterprises(enterprises.sort());
      this.enterprises = enterprises.sort();
      this.enterpriseIdSelected = this.enterprises[Constant.ZERO_INDEX].id;
    });
  }

  /**
   * Filtra la empresa al seleccionar el partner
   * @param partnerId
   * @private
   */
  private getEnterpriseBy(partnerId: number): void {
    const enters = this.enterpriseDataService.getEnterprises();
    if (this.hasRole(this.ROLE_SUPERADMIN)) {
      if (partnerId === Constant.ALL_PARTNER_ID) {
        this.enterprises = LocalStorage.getDefaultEnterprise();
      }
      const enterprisesFilter = enters.filter(value => partnerId === value.partnerId);
      this.enterprises = LocalStorage.addEnterprise(enterprisesFilter);
      this.enterpriseIdSelected = this.enterprises[Constant.ZERO_INDEX].id;
    }
  }
}
